import axios from 'axios';
import {
  RULES_FETCH_REQUEST,
  RULES_FETCH_SUCCESS,
  RULES_FETCH_FAILURE,
  RULE_SAVE_REQUEST,
  RULE_SAVE_SUCCESS,
  RULE_SAVE_FAILURE,
  RULE_DELETE_SUCCESS,
  RULE_TOGGLE_SUCCESS,
  RULES_REORDER_SUCCESS,
} from '../constants';
import { getAuthHeaders, getBaseUrl } from '../helpers/rulesApiHelpers';

// ─── Fetch all rules ──────────────────────────────────────────────────────────

export function fetchRules() {
  return async (dispatch) => {
    dispatch({ type: RULES_FETCH_REQUEST });
    try {
      const url = await getBaseUrl();
      const headers = await getAuthHeaders();
      const response = await axios.get(`${url}/api/v1/rules_engine/rules.json`, { headers });
      dispatch({ type: RULES_FETCH_SUCCESS, payload: response.data.rules || [] });
    } catch (error) {
      const status = error?.response?.status;
      const payload = status === 403
        ? { message: 'Automation Rules is not enabled for your account. Please contact your administrator.', forbidden: true }
        : { message: error?.message || 'Failed to load rules', forbidden: false };
      dispatch({ type: RULES_FETCH_FAILURE, payload });
    }
  };
}

// ─── Save rule (create or update) ─────────────────────────────────────────────

export function saveRule(ruleData) {
  return async (dispatch) => {
    dispatch({ type: RULE_SAVE_REQUEST });
    try {
      const url = await getBaseUrl();
      const headers = await getAuthHeaders();
      const method = ruleData.id ? 'put' : 'post';
      const endpoint = ruleData.id
        ? `${url}/api/v1/rules_engine/rules/${ruleData.id}.json`
        : `${url}/api/v1/rules_engine/rules.json`;

      const response = await axios[method](endpoint, { rule: ruleData }, { headers });
      dispatch({ type: RULE_SAVE_SUCCESS, payload: response.data.rule });
      return { success: true };
    } catch (error) {
      const errors = error?.response?.data?.errors || { base: ['Failed to save rule'] };
      dispatch({ type: RULE_SAVE_FAILURE, payload: errors });
      return { success: false, errors };
    }
  };
}

// ─── Delete rule ──────────────────────────────────────────────────────────────

export function deleteRule(ruleId) {
  return async (dispatch) => {
    try {
      const url = await getBaseUrl();
      const headers = await getAuthHeaders();
      await axios.delete(`${url}/api/v1/rules_engine/rules/${ruleId}.json`, { headers });
      dispatch({ type: RULE_DELETE_SUCCESS, payload: ruleId });
      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to delete rule' };
    }
  };
}

// ─── Toggle rule enabled/disabled ─────────────────────────────────────────────

export function toggleRule(ruleId, enabled) {
  return async (dispatch) => {
    // Optimistic update
    dispatch({ type: RULE_TOGGLE_SUCCESS, payload: { ruleId, enabled } });
    try {
      const url = await getBaseUrl();
      const headers = await getAuthHeaders();
      await axios.patch(
        `${url}/api/v1/rules_engine/rules/${ruleId}.json`,
        { rule: { enabled } },
        { headers }
      );
    } catch (error) {
      // Revert on failure
      dispatch({ type: RULE_TOGGLE_SUCCESS, payload: { ruleId, enabled: !enabled } });
    }
  };
}

// ─── Reorder rules ────────────────────────────────────────────────────────────

export function reorderRules(orderedIds) {
  return async (dispatch, getState) => {
    const previousItems = getState().rules.items;
    // Optimistic update
    dispatch({ type: RULES_REORDER_SUCCESS, payload: orderedIds });
    try {
      const url = await getBaseUrl();
      const headers = await getAuthHeaders();
      await axios.put(
        `${url}/api/v1/rules_engine/rules/reorder.json`,
        { rule_ids: orderedIds },
        { headers }
      );
      return { success: true };
    } catch (error) {
      // Revert on failure
      dispatch({ type: RULES_FETCH_SUCCESS, payload: previousItems });
      return { success: false };
    }
  };
}

// ─── Duplicate rule ───────────────────────────────────────────────────────────

export function duplicateRule(ruleId) {
  return async (dispatch, getState) => {
    const state = getState();
    const original = state.rules.items.find((r) => r.id === ruleId);
    if (!original) return { success: false };

    const copy = {
      name: `${original.name} (copy)`,
      conditions_json: original.conditions_json,
      actions_json: original.actions_json,
      logic: original.logic,
      enabled: false,
    };
    return dispatch(saveRule(copy));
  };
}
